/**
 * The metrics page's capacity band (TRA-1461). Replica history exists only
 * for a model on the dedicated fleet, and the gateway keeps seven days of
 * it. The headline is the live count Baseten reports at request time
 * (`active_replicas`); the sampled history draws the chart only, because
 * the newest sample can predate a scaling change. When the page moves or
 * zooms the stretch (TRA-1592) the chart draws its part of the seven days,
 * and past them one line says there is no history; a drag across it zooms
 * the page.
 */

import { ReplicaUsageChart } from '@/components/models/home/BasetenConfigPanel'
import type { PagePan } from '@/components/models/home/PanControls'
import type { ReplicaView } from '@/components/models/home/useReplicaView'
import { Headline } from '@/components/models/metrics/MetricsCharts'
import { pastReplicaHistory, replicaHistoryCut } from '@/components/models/metrics/metricsPan'
import { Skeleton } from '@/components/ui/skeleton'
import { useLastDefined } from '@/hooks/useLastDefined'
import { useModelScaling } from '@/hooks/useModels'

export function Capacity({
  modelId,
  hours,
  end,
  view,
  pan,
  isBaseten,
  routeLoaded,
  routeFailed,
}: {
  modelId: string
  hours: number
  /** Where the window ends once the page moved it back; null at now. */
  end: number | null
  /** Where the page looks, over the 7 days of history, with the page's zoom and arrows behind it. */
  view: ReplicaView
  /** The page's arrows and Reset zoom, drawn on this chart like on every other. */
  pan: PagePan
  isBaseten: boolean
  /** The route lookup answered, with a route or an error. */
  routeLoaded: boolean
  /** The route lookup failed, so whether the model is on our fleet is unknown. */
  routeFailed: boolean
}): React.JSX.Element {
  const scaling = useModelScaling(modelId, isBaseten, view.loadHours)
  // A move asks for a longer window; the headline keeps its last value until it lands.
  const data = useLastDefined(scaling.data)
  if (!routeLoaded) return <Skeleton className="h-28 w-full" />
  if (routeFailed) {
    return <p className="text-sm text-destructive">Failed to load the model's route.</p>
  }
  if (!isBaseten) {
    return (
      <p className="text-sm text-muted-foreground">
        This model has no TrainLoop deployment, so there is no replica count to show.
      </p>
    )
  }
  if (scaling.isError) {
    return <p className="text-sm text-destructive">Failed to load replica history.</p>
  }
  if (data === undefined) return <Skeleton className="h-28 w-full" />
  // Baseten can omit the count from the deployment object; a sampled count
  // is not "now", so the band then shows the chart alone.
  const live = data.active_replicas
  return (
    <div className="space-y-4">
      {live !== null && <Headline value={live.toLocaleString()} label="replicas now" />}
      {replicaHistoryCut(end, hours) && (
        <p className="text-sm text-muted-foreground">
          Baseten keeps 7 days of replica history, so this shows the part of the window inside
          those 7 days.
        </p>
      )}
      {pastReplicaHistory(end) ? (
        <p className="text-sm text-muted-foreground">No replica history this far back.</p>
      ) : (
        <ReplicaUsageChart modelId={modelId} view={view} pagePan={pan} />
      )}
    </div>
  )
}
